import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, MapPin, Phone, Clock, Calendar } from 'lucide-react';
import Header from '../../components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { centersAPI } from '../../utils/api';

const AdminCenters = () => {
  const navigate = useNavigate();
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCenter, setEditingCenter] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    country: 'South Korea',
    contact: '',
    email: '',
    working_hours_start: '09:00',
    working_hours_end: '17:00',
    daily_capacity: 50,
    slot_duration_minutes: 30,
    working_days: [0, 1, 2, 3, 4]
  });

  useEffect(() => {
    loadCenters();
  }, []);

  const loadCenters = async () => {
    try {
      const response = await centersAPI.getAll(true);
      setCenters(response.data);
    } catch (error) {
      toast.error('Failed to load centers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCenter) {
        await centersAPI.update(editingCenter.id, formData);
        toast.success('Center updated successfully');
      } else {
        await centersAPI.create(formData);
        toast.success('Center created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      loadCenters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (center) => {
    setEditingCenter(center);
    setFormData({
      name: center.name,
      address: center.address,
      city: center.city,
      country: center.country || 'South Korea',
      contact: center.contact,
      email: center.email || '',
      working_hours_start: center.working_hours_start || '09:00',
      working_hours_end: center.working_hours_end || '17:00',
      daily_capacity: center.daily_capacity || 50,
      slot_duration_minutes: center.slot_duration_minutes || 30,
      working_days: center.working_days || [0, 1, 2, 3, 4]
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (centerId) => {
    if (window.confirm('Are you sure you want to delete this center?')) {
      try {
        await centersAPI.delete(centerId);
        toast.success('Center deleted successfully');
        loadCenters();
      } catch (error) {
        toast.error('Failed to delete center');
      }
    }
  };

  const resetForm = () => {
    setEditingCenter(null);
    setFormData({
      name: '',
      address: '',
      city: '',
      country: 'South Korea',
      contact: '',
      email: '',
      working_hours_start: '09:00',
      working_hours_end: '17:00',
      daily_capacity: 50,
      slot_duration_minutes: 30,
      working_days: [0, 1, 2, 3, 4]
    });
  };

  const toggleWorkingDay = (day) => {
    setFormData(prev => ({
      ...prev,
      working_days: prev.working_days.includes(day)
        ? prev.working_days.filter(d => d !== day)
        : [...prev.working_days, day].sort()
    }));
  };

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-secondary">
      <Header />
      
      <div className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-primary mb-2">
                Centers Management
              </h1>
              <p className="text-muted-foreground">
                Manage service centers, working hours, and capacity
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => navigate('/admin')}>
                Back to Dashboard
              </Button>
              <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} data-testid="add-center-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Center
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {centers.map((center) => (
              <div
                key={center.id}
                className={`bg-white rounded-lg border p-6 shadow-sm ${!center.is_active ? 'opacity-60' : ''}`}
                data-testid={`center-card-${center.id}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold text-primary">{center.name}</h3>
                  <div className="flex gap-2">
                    <Button size="icon" variant="ghost" onClick={() => handleEdit(center)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => handleDelete(center.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <span>{center.address}, {center.city}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{center.contact}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>{center.working_hours_start || '09:00'} - {center.working_hours_end || '17:00'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>Capacity: {center.daily_capacity || 50}/day</span>
                  </div>
                </div>

                {!center.is_active && (
                  <div className="mt-4 text-xs text-red-500 font-medium">Inactive</div>
                )}
              </div>
            ))}
          </div>

          {centers.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No centers found. Create your first center.</p>
            </div>
          )}
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCenter ? 'Edit Center' : 'Add New Center'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Center Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="address">Address *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                />
              </div>
              
              <div>
                <Label htmlFor="contact">Contact Number *</Label>
                <Input
                  id="contact"
                  value={formData.contact}
                  onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              
              <div>
                <Label htmlFor="working_hours_start">Working Hours Start</Label>
                <Input
                  id="working_hours_start"
                  type="time"
                  value={formData.working_hours_start}
                  onChange={(e) => setFormData({ ...formData, working_hours_start: e.target.value })}
                />
              </div>
              
              <div>
                <Label htmlFor="working_hours_end">Working Hours End</Label>
                <Input
                  id="working_hours_end"
                  type="time"
                  value={formData.working_hours_end}
                  onChange={(e) => setFormData({ ...formData, working_hours_end: e.target.value })}
                />
              </div>
              
              <div>
                <Label htmlFor="daily_capacity">Daily Capacity</Label>
                <Input
                  id="daily_capacity"
                  type="number"
                  min="1"
                  value={formData.daily_capacity}
                  onChange={(e) => setFormData({ ...formData, daily_capacity: parseInt(e.target.value) })}
                />
              </div>
              
              <div>
                <Label htmlFor="slot_duration">Slot Duration (minutes)</Label>
                <Select
                  value={String(formData.slot_duration_minutes)}
                  onValueChange={(value) => setFormData({ ...formData, slot_duration_minutes: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="45">45 minutes</SelectItem>
                    <SelectItem value="60">60 minutes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="col-span-2">
                <Label>Working Days</Label>
                <div className="flex gap-2 mt-2">
                  {dayNames.map((day, index) => (
                    <Button
                      key={day}
                      type="button"
                      variant={formData.working_days.includes(index) ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => toggleWorkingDay(index)}
                    >
                      {day}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-center-btn">
                {editingCenter ? 'Update' : 'Create'} Center
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCenters;
